import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { observer } from 'mobx-react-lite';
import reasonListStore from '../../stores/reason-list-store';
import EditorPageLayout from '../../../../components/layouts/editor-page/editor-page-layout';
import { LABEL_POSITIONS, Toggle } from '../../../../components/inputs/toggle/toggle';
import ControlGroup from '../../../../components/layouts/control-group/control-group';
import { FormField } from '../../../../components/form-field/form-field';
import TranslationInput from '../../../../translation/translation-input/translation-input';
import { SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import FUQList from './fuq-list/fuq-list';
import usePage from '../../../../components/layouts/base-page-layout/page-hook';
import useTranslation from '../../../../translation/hooks/translation-hook';
import { reasonTypeList, RETURN_REASON_TYPE, RETURN_REASON_TYPE_MAP } from '../../constants';
import useNavigationBlocker from '../../../../hooks/navigation-blocker-hook';
import { ConfirmModal } from '../../../../components/modal/components/confirm-modal/confirm-modal';
import { NotificationModal } from '../../../../components/modal/components/notification-modal/notification-modal';
import useValidationGroup from '../../../../hooks/validation-group/validation-group-hook';
import FontBody2 from '../../../../components/fonts/font-body2/font-body2';
import { FONT_COLOR } from '../../../../components/fonts/font-props';
import { Button } from '../../../../components/inputs/button/button';
import { BUTTON_VARIANTS } from '../../../../components/inputs/button-variants';
import IconPlus from '../../../../components/icons/icon-plus/icon-plus';
import './return-reason.scss';
import FollowUpQuestionStore from '../../stores/follow-up-question-store';
import SwitchToExchange from './switch-to-exchange/switch-to-exchange';
import ExternalIntegrationSection from './external-integration-section/external-integration-section';


const ReturnReason: React.FC = observer(() => {
    const navigate = useNavigate();
    const vGroup = useValidationGroup();
    const params = useParams();
    const { setSubTitles } = usePage();
    const { 
        getTranslation, hasChanges: hasTranslationChanges, 
        save: saveTranslations, isSaving: isSavingTranslations,
        discardChanges: translationsDiscardChanges,
        defaultLocale, selectedLocale, setTranslatableKeys        
    } = useTranslation();
    const reasonId = params.reasonId as string|number;
    const reason = reasonListStore.selectedReturnReason;
    const selectedReasonType = RETURN_REASON_TYPE_MAP[reason?.type as RETURN_REASON_TYPE];
    const translatedSubtitle = getTranslation(reason?.displayName as string);
    const [isShowUnsaved, setIsShowUnsaved] = useState(false);
    const [isShowNotification, setIsShowNotification] = useState(false);
    const { setDeletedKeys } = useTranslation();

    const hasChanges = (): boolean =>
        (reason?.hasChanges() ?? false) || hasTranslationChanges();

    const discardChanges = () => {
        reason?.discardChanges();
        translationsDiscardChanges();
    }

    const { retryNavigation, unblockNavigation } = useNavigationBlocker(
        () => hasChanges(),
        useCallback(() => setIsShowUnsaved(true), [setIsShowUnsaved])
    );
    
    
    useEffect(() => {
        reasonListStore.setSelectedReasonId(reasonId);
    }, [reasonId]);

    useEffect(() => {
        setSubTitles(['Return Reasons', translatedSubtitle]);
    }, [setSubTitles, translatedSubtitle]);

    const keys = reason ? reason.getTranslationKeys() : null;
    useEffect(() => {
        if (keys) {
            setTranslatableKeys(keys);
        }
        return () => setTranslatableKeys([]);
    }, [setTranslatableKeys, keys]);

    
    const onSaveClick = () => {
        const promised = [];

        if (reason?.hasChanges()) {
            promised.push(
                reason?.save()
            );
        }      
        if (hasTranslationChanges()) {
            promised.push(
                saveTranslations()
            );            
        }

        Promise.all(promised).then(() => {  
            unblockNavigation();
            navigate({
                pathname: `/return-reasons/${reason?.id}`,
            }, { replace: true });          
        })
    }

    const onQuestionDelete = (question: FollowUpQuestionStore) => {
        question.setDeleted(setDeletedKeys);
        onSaveClick();
    }

    const toggleReason = (isOn:boolean) => {
        if (!isOn) {
            if (reason?.canDisable()) {
                reason?.setIsEnabled(isOn);
            }
            else {
                setIsShowNotification(true);
            }
        }
        else {
            reason?.setIsEnabled(isOn);
        }
    }

    const externalReasonSource = "Amazon MCF";
    const selectedExternalReason = (reasonListStore.settings.mcf_return_reasons ?? []).find(r => r.value === reason?.externalReasonCode);

    return (
        <>
        <EditorPageLayout
            toolbar={{
                content: (
                    <Toggle 
                        checked={reason?.isEnabled} 
                        label="Display reason" 
                        labelPosition={LABEL_POSITIONS.LEFT}
                        onChange={toggleReason} 
                    />
                ),
                saveButton: {
                    hasChanges: hasChanges(),
                    onClick: () => onSaveClick(),
                    disabled: !vGroup.isValid() || !hasChanges() || isSavingTranslations(),
                    isValid: vGroup.isValid(),
                },
            }}
        >

            <ControlGroup>
                <FormField label="Reason display name" required>
                    <TranslationInput translationKey={reason?.displayName} required validationGroup={vGroup} />
                </FormField>
                <FormField 
                    label="Reason category" 
                    tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                        Categorize the reason for high-level reporting. <br /> 
                        In the future, it will help us to provide you <br /> 
                        with industry-wide insights.
                    </FontBody2>} 
                    required
                >
                    <SelectDropdown
                        value={selectedReasonType}
                        options={reasonTypeList()}
                        onChange={e => reason?.setType(e?.value as RETURN_REASON_TYPE)}
                        disabled={selectedLocale !== defaultLocale}
                        required
                        validationGroup={vGroup}
                    />
                </FormField>
                {reasonListStore.settings.mcf_return_reasons?.length > 0 &&
                    <FormField 
                        label={`${externalReasonSource} Reason`} 
                        tooltip={<FontBody2 color={FONT_COLOR.NEUTRAL_WHITE}>
                            For the integration to work properly, match your <br/>
                            return reason with an appropriate one of {externalReasonSource}
                        </FontBody2>}                     
                    >
                        <SelectDropdown
                            value={selectedExternalReason}
                            options={reasonListStore.settings.mcf_return_reasons}
                            onChange={e => reason?.setExternalReasonCode(e?.value as string)}
                            placeholder='Please select'
                            required
                            validationGroup={vGroup}
                        />
                    </FormField>
                }
            </ControlGroup>

	        <SwitchToExchange
		        isEnabled={reason?.isSwitchToExchangeEnabled}
		        exchangeTypes={reason?.exchangeTypes}
		        onToggle={() => reason?.setIsSwitchToExchangeEnabled(!reason?.isSwitchToExchangeEnabled)}
		        onExchangeTypeChange={(exchangeType: string) => reason?.onExchangeTypeChange(exchangeType)}
	        />

            <FUQList list={reason?.followUpQuestions} onQuestionDelete={onQuestionDelete} />

            <div className="add-question-button">
                <Button
                    variant={BUTTON_VARIANTS.Text}
                    text="add question"
                    leftIcon={IconPlus}
                    disabled={hasChanges()}
                    onClick={() => {
                        const questionClientId = reason?.addFollowUpQuestion();
                        navigate({
                            pathname: questionClientId
                        });
                    }}
                />
            </div>

            {reasonListStore.settings.should_show_reason_code_input && reason &&
                <ExternalIntegrationSection reason={reason} validationGroup={vGroup} />
            }
        </EditorPageLayout>

        { isShowUnsaved && 
            <ConfirmModal 
                cancelButton={{ 
                    title: 'Cancel', 
                    onClick: () => setIsShowUnsaved(false) 
                }} 
                okButton={{ 
                    title: 'Discard', 
                    onClick: () => { 
                        discardChanges();
                        setIsShowUnsaved(false); 
                        retryNavigation();
                    }
                }}
            />
        }
        
        { isShowNotification &&
            <NotificationModal 
                text="Cannot disable reason, at least one reason must be enabled." 
                okButton={{
                    title: 'ok',
                    onClick: () => setIsShowNotification(false)
                }}
            />
        }
        </>
    );
})

export default ReturnReason;
